import { parseLoanInstallmentExternalId } from '@progear/finance-core';
import {
  useInvoiceLoanLinkContext,
  useLinkLoanInvoice,
  useLoanInstallmentInvoiceCandidates,
  useLoanInstallmentInvoices,
  useUnlinkLoanInvoice,
} from '@/hooks/useCashFlow';
import type { CashFlowEntry, CashFlowRequestOptions } from '@/lib/cashFlow';
import {
  allocationCentsOf,
  componentEntryIdOf,
  effectiveEntrySettlement,
  type LoanInvoiceAllocationInput,
  type NormalizedLoanInvoiceCandidate,
  type NormalizedLoanInvoiceRelation,
} from '@/lib/loanInvoiceLinks';
import { LoanInvoiceLinkPanel } from './LoanInvoiceLinkPanel';

interface Props {
  entry: CashFlowEntry;
  requestOptions?: CashFlowRequestOptions;
  onOpenEntry?: (entryId: string) => void;
}

const reasonLabels: Record<string, string> = {
  party_exact: 'sama osapool',
  party_partial: 'sarnane osapool',
  due_date_exact: 'sama tähtaeg',
  due_date_near: 'lähedane tähtaeg',
  amount_exact: 'sama summa',
};

export function LoanInvoiceLinkSection({ entry, requestOptions, onOpenEntry }: Props) {
  const ref = parseLoanInstallmentExternalId(entry.externalId);
  const isInvoice =
    entry.source === 'smartaccounts' && entry.kind === 'cost' && entry.docType === 'invoice';
  const loanId = ref?.loanId ?? '';
  const seq = ref?.seq ?? 0;
  const linked = useLoanInstallmentInvoices(loanId, seq, requestOptions);
  const candidateData = useLoanInstallmentInvoiceCandidates(loanId, seq, requestOptions);
  const invoiceContext = useInvoiceLoanLinkContext(entry.id, isInvoice, requestOptions);
  const link = useLinkLoanInvoice(requestOptions);
  const unlink = useUnlinkLoanInvoice(requestOptions);

  if (ref === null && !isInvoice) return null;

  const dueDate = candidateData.data?.installmentDueDate ?? entry.expectedDate;
  const relations: NormalizedLoanInvoiceRelation[] = ref
    ? (linked.data?.linkedInvoices ?? []).map((relation) => {
        const settled = effectiveEntrySettlement(relation.entry);
        return {
          linkId: relation.linkId,
          loanId,
          seq,
          lender: entry.party,
          dueDate,
          loanEntryId: componentEntryIdOf(relation.allocations, ref.part),
          invoiceEntryId: relation.entryId,
          invoiceDocNumber: relation.invoiceDocNumber,
          invoiceParty: relation.invoiceParty,
          invoiceGrossCents: relation.invoiceGrossCents,
          invoiceSettledCents: settled,
          principalCents: allocationCentsOf(relation.allocations, 'principal'),
          interestCents: allocationCentsOf(relation.allocations, 'interest'),
          feeCents: allocationCentsOf(relation.allocations, 'fee'),
          canUnlink: relation.canUnlink,
          unlinkBlockedReason: relation.canUnlink
            ? null
            : 'Arvel on tasumisajalugu; seost ei saa eemaldada.',
        };
      })
    : invoiceContext.data?.relation
      ? [{
          linkId: invoiceContext.data.relation.linkId,
          loanId: invoiceContext.data.relation.loanId,
          seq: invoiceContext.data.relation.seq,
          lender: invoiceContext.data.relation.lender ?? 'Laen',
          dueDate: invoiceContext.data.relation.installmentDueDate ?? entry.expectedDate,
          loanEntryId:
            invoiceContext.data.relation.loanEntryId
            ?? invoiceContext.data.relation.componentEntryIds.interest
            ?? invoiceContext.data.relation.componentEntryIds.fee
            ?? '',
          invoiceEntryId: entry.id,
          invoiceDocNumber: entry.docNumber,
          invoiceParty: entry.party,
          invoiceGrossCents: entry.money.gross,
          invoiceSettledCents: effectiveEntrySettlement(entry),
          principalCents: invoiceContext.data.relation.allocation.principalCents,
          interestCents: invoiceContext.data.relation.allocation.interestCents,
          feeCents: invoiceContext.data.relation.allocation.feeCents,
          canUnlink: invoiceContext.data.relation.canUnlink,
          unlinkBlockedReason: invoiceContext.data.relation.canUnlink
            ? null
            : 'Arvel on tasumisajalugu; seost ei saa eemaldada.',
        }]
      : [];

  const capacity = candidateData.data?.capacity;
  const loanSideCandidates: NormalizedLoanInvoiceCandidate[] = capacity
    ? candidateData.data!.candidates.map((candidate) => ({
        id: `${loanId}:${seq}:${candidate.entryId}`,
        loanId,
        seq,
        lender: entry.party,
        dueDate,
        loanEntryId: entry.id,
        invoiceEntryId: candidate.entryId,
        invoiceDocNumber: candidate.entry.docNumber,
        invoiceParty: candidate.entry.party,
        invoiceGrossCents: candidate.grossCents,
        invoiceSettledCents: effectiveEntrySettlement(candidate.entry),
        availablePrincipalCents: capacity.principal.remainingCents,
        availableInterestCents: capacity.interest.remainingCents,
        availableFeeCents: capacity.fee.remainingCents,
        proposedPrincipalCents: candidate.proposedAllocation.principalCents,
        proposedInterestCents: candidate.proposedAllocation.interestCents,
        proposedFeeCents: candidate.proposedAllocation.feeCents,
        exactMatch: candidate.exactMatch,
        reasons: candidate.reasons.map((reason) => reasonLabels[reason] ?? reason),
      }))
    : [];
  const invoiceSideCandidates: NormalizedLoanInvoiceCandidate[] = (
    invoiceContext.data?.candidates ?? []
  ).flatMap((candidate) => {
    const loanEntryId =
      candidate.loanEntryId
      ?? candidate.componentEntryIds.interest
      ?? candidate.componentEntryIds.fee;
    if (loanEntryId === null) return [];
    return [{
      id: `${candidate.loanId}:${candidate.seq}:${entry.id}`,
      loanId: candidate.loanId,
      seq: candidate.seq,
      lender: candidate.lender,
      dueDate: candidate.installmentDueDate,
      loanEntryId,
      invoiceEntryId: entry.id,
      invoiceDocNumber: entry.docNumber,
      invoiceParty: entry.party,
      invoiceGrossCents: entry.money.gross,
      invoiceSettledCents: effectiveEntrySettlement(entry),
      availablePrincipalCents: candidate.capacity.principal.remainingCents,
      availableInterestCents: candidate.capacity.interest.remainingCents,
      availableFeeCents: candidate.capacity.fee.remainingCents,
      proposedPrincipalCents: candidate.proposedAllocation.principalCents,
      proposedInterestCents: candidate.proposedAllocation.interestCents,
      proposedFeeCents: candidate.proposedAllocation.feeCents,
      exactMatch: candidate.exactMatch,
      reasons: candidate.reasons.map((reason) => reasonLabels[reason] ?? reason),
    }];
  });
  const candidates = ref ? loanSideCandidates : invoiceSideCandidates;

  const error =
    linked.error instanceof Error
      ? linked.error.message
      : candidateData.error instanceof Error
        ? candidateData.error.message
        : invoiceContext.error instanceof Error
          ? invoiceContext.error.message
          : link.error instanceof Error
            ? link.error.message
            : unlink.error instanceof Error
              ? unlink.error.message
              : null;

  const handleLink = (
    candidate: NormalizedLoanInvoiceCandidate,
    allocations: LoanInvoiceAllocationInput,
  ) => {
    link.mutate({
      loanId: candidate.loanId,
      seq: candidate.seq,
      entryId: candidate.invoiceEntryId,
      ...allocations,
      idempotencyKey: crypto.randomUUID(),
    });
  };

  return (
    <LoanInvoiceLinkPanel
      currentEntryId={entry.id}
      relations={relations}
      candidates={candidates}
      isLoading={ref ? linked.isLoading || candidateData.isLoading : invoiceContext.isLoading}
      isLinking={link.isPending}
      isUnlinking={unlink.isPending}
      error={error}
      onLink={handleLink}
      onUnlink={(relation) =>
        unlink.mutate({
          loanId: relation.loanId,
          seq: relation.seq,
          entryId: relation.invoiceEntryId,
        })
      }
      onOpenEntry={onOpenEntry}
    />
  );
}